import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogContent,
  IconButton,
  Box,
  Typography,
  useTheme,
  Fade,
} from '@mui/material';
import { Close, ChevronLeft, ChevronRight } from '@mui/icons-material';
import { GalleryMediaItem } from '../../services/api';

interface GalleryLightboxProps {
  open: boolean;
  media: GalleryMediaItem[];
  initialIndex?: number;
  onClose: () => void;
}

const GalleryLightbox: React.FC<GalleryLightboxProps> = ({
  open,
  media,
  initialIndex = 0,
  onClose,
}) => {
  const theme = useTheme();
  const [index, setIndex] = useState(initialIndex);

  const items = useMemo(
    () => (media ?? []).filter((item) => !!item?.image_url),
    [media]
  );

  useEffect(() => {
    if (open) {
      setIndex(initialIndex);
    }
  }, [open, initialIndex]);

  useEffect(() => {
    if (!open) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        goPrevious();
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        goNext();
      } else if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open, index, onClose, items.length]);

  const goPrevious = () => {
    if (items.length === 0) return;
    setIndex((prev) => (prev === 0 ? items.length - 1 : prev - 1));
  };

  const goNext = () => {
    if (items.length === 0) return;
    setIndex((prev) => (prev === items.length - 1 ? 0 : prev + 1));
  };

  const currentItem = items[index];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: 'rgba(0, 0, 0, 0.92)',
          color: '#fff',
        },
      }}
    >
      <DialogContent
        sx={{
          p: 0,
          position: 'relative',
          height: { xs: '80vh', sm: '85vh' },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
        }}
      >
        <IconButton
          onClick={onClose}
          sx={{
            position: 'absolute',
            top: 16,
            right: 16,
            color: '#fff',
            backgroundColor: 'rgba(0,0,0,0.4)',
            '&:hover': {
              backgroundColor: 'rgba(0,0,0,0.6)',
            },
          }}
        >
          <Close />
        </IconButton>

        {items.length > 1 && (
          <>
            <IconButton
              onClick={goPrevious}
              sx={{
                position: 'absolute',
                left: 16,
                color: '#fff',
                backgroundColor: 'rgba(0,0,0,0.4)',
                '&:hover': {
                  backgroundColor: 'rgba(0,0,0,0.6)',
                },
              }}
            >
              <ChevronLeft />
            </IconButton>

            <IconButton
              onClick={goNext}
              sx={{
                position: 'absolute',
                right: 16,
                color: '#fff',
                backgroundColor: 'rgba(0,0,0,0.4)',
                '&:hover': {
                  backgroundColor: 'rgba(0,0,0,0.6)',
                },
              }}
            >
              <ChevronRight />
            </IconButton>
          </>
        )}

        {currentItem ? (
          <Fade in={open} key={currentItem.id}>
            <Box
              sx={{
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                p: { xs: 2, sm: 4 },
              }}
            >
              <Box
                component="img"
                src={currentItem.image_url}
                alt={currentItem.caption || 'Gallery photo'}
                sx={{
                  maxWidth: '100%',
                  maxHeight: '100%',
                  borderRadius: 2,
                  boxShadow: `0 24px 48px rgba(0, 0, 0, 0.4)`,
                  objectFit: 'contain',
                  backgroundColor: theme.palette.common.black,
                }}
              />
            </Box>
          </Fade>
        ) : (
          <Box
            sx={{
              color: 'rgba(255,255,255,0.7)',
              textAlign: 'center',
              px: 3,
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 500 }}>
              No photos available
            </Typography>
            <Typography variant="body2">
              Add some photos to your gallery to showcase your adventures.
            </Typography>
          </Box>
        )}

        {currentItem && (
          <Box
            sx={{
              position: 'absolute',
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
              px: { xs: 3, sm: 6 },
              py: { xs: 2, sm: 3 },
              background: 'linear-gradient(to top, rgba(0,0,0,0.75), rgba(0,0,0,0))',
            }}
          >
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              {currentItem.caption || 'Photo'}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.8 }}>
              {index + 1} / {items.length}
            </Typography>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default GalleryLightbox;

